import {useMediaQuery} from '@mui/material'
import {FC, useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {setProfileChatOpen} from 'entities/app'
import {
  getChatPair,
  GetChatRequest,
  GetDialog,
  PinUnpinDialog,
  setChatRequest,
  setMockDialog,
  setMockDialogChatRequest,
  setOpenDialog,
  useDialogsUnreadCount,
  useOpenDialog
} from 'entities/messages'
import {GetUserInfo, setProfile, useProfile} from 'entities/profile'
import {TooltipConfirmed} from 'features/tooltip-confirmed'
import {mediaVariants} from 'shared/lib'
import {displayCount} from 'shared/lib/entity'
import {Avatar} from 'shared/ui/avatar'
import {ArrowLeft, Dots, Hot, Pin, PinActive} from 'shared/ui/icons'
import {Tooltip} from 'shared/ui/tooltip'
import {ExtraDropdown} from 'widgets/extraDropdown'
import styles from './styles.module.scss'

type ChatHeaderProps = {
  idUser: string
}

export const ChatHeader: FC<ChatHeaderProps> = ({idUser}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const openDialog = useOpenDialog()
  const profile = useProfile()
  const {refetch: getDialog, isFetching} = GetDialog(idUser, dispatch)
  const {refetch: pinUnpinDialog} = PinUnpinDialog(idUser, !openDialog?.isPinned ?? false, dispatch)
  const [isExtraOpen, setIsExtraOpen] = useState(false)
  const {refetch: loadChatPair} = getChatPair(idUser, dispatch)
  const is1280p = useMediaQuery(`(max-width: ${styles.is1280p})`)
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)
  const unreadMessages = useDialogsUnreadCount()

  useEffect(() => {
    loadChatPair().catch(console.error)
  }, [loadChatPair, idUser])

  useEffect(() => {
    if (openDialog && openDialog.interlocutor.id === idUser) return

    dispatch(setOpenDialog(null))
  }, [openDialog, idUser])

  useEffect(() => {
    if (openDialog) return

    getDialog()
      .then(({data}) => {!data?.data && GetChatRequest(idUser).then(({data}) => {
        dispatch(setMockDialogChatRequest(data))
        dispatch(setChatRequest(data))
      })})
      .catch(console.error)
  }, [openDialog, idUser, getDialog])

  useEffect(() => {
    if (!isFetching && openDialog === null && profile.id) {
      dispatch(setMockDialog(profile))
    }
  }, [openDialog, profile, isFetching])

  useEffect(() => {
    GetUserInfo(idUser)
      .then(({data}) => dispatch(setProfile(data)))
      .catch(console.error)
  }, [GetUserInfo, idUser])

  const onPinIcon = () => {
    if (!openDialog) return
    pinUnpinDialog().catch(console.error)
  }

  const openMessages = () => navigate('/messages')

  const openProfile = () => {
    if (profile.isBlocked) return

    if (is1280p) {
      navigate(`/profile/${openDialog ? openDialog.interlocutor.id : profile.id}`)
      return
    }

    dispatch(setProfileChatOpen(true))
  }

  const isOnline = useMemo(() => openDialog ? openDialog.interlocutor.isOnline : profile.isOnline, [openDialog, profile])

  return (
    <div className={styles.chatHeader}>
      <div className={styles.chatHeader__profile}>
        {is768p ?
          <div className={`${styles.backDialogs} ${(!unreadMessages ? styles.notMessages : '')}`} onClick={openMessages}>
            <div className={styles.arrowLeft}>
              <ArrowLeft />
            </div>
            {unreadMessages
              ? <div className={styles.unreadMessages}>{displayCount(unreadMessages)}</div>
              : null}
          </div>
          : null}
        <div
          className={`${styles.chatHeader__profile__summary} ${!profile.isBlocked ? styles.hover : ''}`}
          onClick={openProfile}
        >
          <Avatar
            src={(openDialog ? openDialog.interlocutor.avatarUrl : profile.avatarUrl) + mediaVariants.square64}
            isOnline={!is768p ? isOnline : false}
          />
          <div>
            <p className={styles.chatHeader__profile__name}>{openDialog ? openDialog.interlocutor.name : profile.name}</p>
            {is768p && isOnline ? <span className={styles.online}>Online</span> : null}
          </div>
          {profile.isConfirmed ? <TooltipConfirmed iconWidth={16} /> : null}
          {profile.isPopular ? <Tooltip title={<div className={styles.tooltipHeader}>Popular member</div>}>
            <div className={styles.chatHeader__profile__icon}><Hot /></div>
          </Tooltip> : null}
        </div>
      </div>
      <div className={styles.chatHeader__profile}>
        {openDialog && !is768p ?
          <div
            className={styles.pinIcon}
            onClick={onPinIcon}
          >
            {openDialog.isPinned ? <PinActive /> : <Pin />}
          </div>
          : null}
        {
          !profile.isBlocked ? <>
            <div
              className={`${styles.chatHeader__extraIcon} ${styles.dotsIcon}`}
              onClick={() => setIsExtraOpen(true)}
            >
              <Dots />
            </div>
            <ExtraDropdown
              isOpen={isExtraOpen}
              onClose={() => setIsExtraOpen(false)}
              user={openDialog ? openDialog.interlocutor : profile}
              style={is768p ? {right: 5, top: 46} : {}}
              isPinDialog={is768p}
            />
          </> : null
        }
      </div>
    </div>
  )
}